import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

interface AnalyticsProps {
  googleAnalyticsId?: string;
  googleSearchConsoleId?: string;
}

const Analytics: React.FC<AnalyticsProps> = ({ 
  googleAnalyticsId = 'GA_MEASUREMENT_ID', // Replace with your actual GA ID
  googleSearchConsoleId = 'GSC_VERIFICATION_ID' // Replace with your actual GSC verification ID
}) => {
  const location = useLocation();

  useEffect(() => {
    // Google Analytics
    if (googleAnalyticsId) {
      // Add Google Analytics script
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${googleAnalyticsId}', {
          page_title: document.title,
          page_location: window.location.href,
          send_page_view: true,
          anonymize_ip: true,
          allow_google_signals: true,
          allow_ad_personalization_signals: false
        });
      `;
      document.head.appendChild(script2);

      // Add Google Search Console verification meta tag
      if (googleSearchConsoleId) {
        const gscMeta = document.createElement('meta');
        gscMeta.name = 'google-site-verification';
        gscMeta.content = googleSearchConsoleId;
        document.head.appendChild(gscMeta);
      }
    }

    // Track page views on route changes
    return () => {
      if (window.gtag) {
        window.gtag('config', googleAnalyticsId, {
          page_title: document.title,
          page_location: window.location.href,
          page_path: location.pathname,
        });
      }
    };
  }, [googleAnalyticsId, googleSearchConsoleId]);

  // Track page views on location change
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', googleAnalyticsId, {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname,
      });
    }
  }, [location, googleAnalyticsId]);

  // Function to track custom events
  const trackEvent = (action: string, category: string, label?: string, value?: number) => {
    if (window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  };

  // Expose trackEvent globally for use in components
  useEffect(() => {
    (window as any).trackEvent = trackEvent;
  }, []);

  return null;
};

export default Analytics;
